import { FC } from 'react';
import { IonButton } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { AlertPresenter } from '../../AlertPresenter';
import { OrganizationsState } from '../OrganizationsState';
import { IOrganization } from '../IOrganization';
import { DemoDataRefreshState } from './DemoDataRefreshState';

const refreshAndReport = async (params: { org: IOrganization; refreshState: DemoDataRefreshState; alertPresenter: AlertPresenter }) => {
    const { org, refreshState, alertPresenter } = params;

    try {
        await refreshState.refresh(org);
    } catch (error) {
        await alertPresenter.showAlertV2({ header: 'Could not refresh demo data', message: 'The caseload could not be loaded. Try again.' });
        throw error;
    }

    const { total, failed, withoutStoryline } = refreshState.state;
    if (total === 0) {
        await alertPresenter.showAlertV2({ header: 'Nothing to refresh', message: `${org.name} has no clients to refresh.` });
        return;
    }

    const seeded = total - withoutStoryline;
    const datedFromToday = withoutStoryline > 0
        ? `${seeded} clients are dated from today. ${withoutStoryline} more ${withoutStoryline === 1 ? 'client has' : 'clients have'} no demo data, as there are more clients than demo storylines.`
        : `All ${total} clients are dated from today.`;

    await alertPresenter.showAlertV2({
        header: 'Demo data refreshed',
        message: failed > 0
            ? `${failed} of ${total} clients could not be refreshed. Refreshing again redoes every client.`
            : `${datedFromToday} The app will reload to show them.`
    });
    window.location.reload();
};

/**
 * Re-seeds the current demo organization's clients so its insights read as a live caseload. Shown
 * only where the api offers it: an Innerhive admin on an organization set up for demos.
 */
export const DemoDataDevTools: FC = () => {
    const orgState = useInstance(OrganizationsState);
    useSubscription(() => orgState);

    const refreshState = useInstance(DemoDataRefreshState);
    useSubscription(() => refreshState);

    const alertPresenter = useInstance(AlertPresenter);

    const org = orgState.getMostRecentOrganization();
    if (!org?.links.demoData) {
        return null;
    }

    const { isRunning, done, total, failed } = refreshState.state;

    const confirmAndRefresh = async () => {
        const choice = await alertPresenter.showAlertV2({
            header: 'Refresh demo data?',
            message: `Replaces the invented notes and findings for every client of ${org.name}, dated back from today, and gives each a stock photo. Anything else on their records stays.`,
            options: [
                { id: 'cancel', label: 'Cancel', role: 'cancel' },
                { id: 'refresh', label: 'Refresh' }
            ]
        });
        if (choice === 'refresh') {
            await refreshAndReport({ org, refreshState, alertPresenter });
        }
    };

    const label = !isRunning
        ? 'Refresh demo data'
        : total === 0
            ? 'Loading the caseload'
            : `Refreshing ${done} of ${total}${failed > 0 ? `, ${failed} failed` : ''}`;

    return (
        <>
            <h5>Demo Data Dev Tools</h5>
            <IonButton fill="outline" size="small" disabled={isRunning} data-id="refresh-demo-data" onClick={confirmAndRefresh}>
                {label}
            </IonButton>
        </>
    );
};
